import React from 'react'

import { Formik } from 'formik'

import { ZariContext } from '_zari/context'

import { Form, Col, Modal, Button, Row, Divider } from '_zari/core'

import { MonedasConstant } from 'constants/index'

import { RounderUtil } from 'utils'

import * as FormGroups from './formGroups'

const FormularioPagosModal = ({ handleClose, show, submitData, submitFunction }) => {
  const __zariContext = React.useContext(ZariContext)

  const importeReCalculado = React.useRef(submitData.importeTotal)

  const formInitialValues = React.useMemo(
    () => ({
      flagCambioMoneda: false,
      medioPago1: '008',
      medioPago2: '',
      medioPago3: '',
      moneda: submitData.moneda || '',
      montoMedio1: submitData.importeTotal || '',
      montoMedio2: '',
      montoMedio3: '',
      montoRecibido: '',
      montoVuelto: '',
      numeroDocumentoMedio2: '',
      numeroDocumentoMedio3: '',
      tipoCambio: 1,
      fechaPago: submitData.fechaEmision,
      medioDePago: '008',
      flujoEfectivo: 'O001'
    }),
    [submitData]
  )

  const clickRecalcular = React.useCallback(
    ({ values, setValues }) => {
      if (values.tipoCambio <= 3) {
        __zariContext.snack('error', 'Verificar el tipo de cambio asignado')
        return
      }

      if (values.moneda !== submitData.moneda) {
        if (submitData.moneda === MonedasConstant.SOLES && values.moneda === MonedasConstant.DOLARES) {
          const nuevoMonto = RounderUtil.round(submitData.importeTotal / values.tipoCambio)
          importeReCalculado.current = nuevoMonto
          setValues({
            ...values,
            montoMedio1: nuevoMonto,
            montoMedio2: '',
            montoMedio3: '',
            montoRecibido: '',
            montoVuelto: ''
          })
        } else if (submitData.moneda === MonedasConstant.DOLARES && values.moneda === MonedasConstant.SOLES) {
          const nuevoMonto = RounderUtil.round(submitData.importeTotal * values.tipoCambio)
          importeReCalculado.current = nuevoMonto
          setValues({
            ...values,
            montoMedio1: nuevoMonto,
            montoMedio2: '',
            montoMedio3: '',
            montoRecibido: '',
            montoVuelto: ''
          })
        } else {
          __zariContext.snack('warning', 'Verificar moneda de pago')
          return
        }
      } else {
        __zariContext.snack('warning', 'Verificar moneda de pago')
        return
      }
    },
    [__zariContext, submitData.importeTotal, submitData.moneda]
  )

  const onSubmit = React.useCallback(
    (formData, { setSubmitting } = {}) => {
      let sumaPagos = 0
      if (formData.montoMedio1) {
        sumaPagos += parseFloat(formData.montoMedio1 || '0')
      }
      if (formData.montoMedio2) {
        sumaPagos += parseFloat(formData.montoMedio2 || '0')
      }
      if (formData.montoMedio3) {
        sumaPagos += parseFloat(formData.montoMedio3 || '0')
      }

      if (sumaPagos !== importeReCalculado.current) {
        __zariContext.snack('error', 'Los pagos no coinciden con el monto total.')
        setSubmitting(false)
        return
      }

      submitFunction(
        {
          ...submitData,
          pago: formData,
          montoRecibido: formData.montoRecibido
        },
        { setSubmitting }
      )
    },
    [__zariContext, submitData, submitFunction]
  )

  React.useEffect(() => {
    const nodoBoton = document.getElementById('boton-guardar-pago')
    nodoBoton.focus()
  }, [])

  return (
    <Modal show={show}>
      <Modal.Header>
        <Modal.Title>FORMULARIO DE PAGOS</Modal.Title>
      </Modal.Header>
      <Formik initialValues={formInitialValues} onSubmit={onSubmit}>
        {({ errors, touched, values, isSubmitting, setFieldValue, setValues, handleSubmit }) => (
          <Form>
            <Modal.Body>
              {values.flagCambioMoneda && (
                <Row>
                  <Col md={3}>
                    <FormGroups.MonedaFormGroup
                      fIsSubmitting={isSubmitting}
                      iTouched={touched.moneda}
                      iError={errors.moneda}
                    />
                  </Col>
                  <Col md={3}>
                    <FormGroups.TipoCambioFormGroup
                      fIsSubmitting={isSubmitting}
                      iTouched={touched.tipoCambio}
                      iError={errors.tipoCambio}
                      iFechaDocumento={submitData.fechaEmision}
                      iMoneda={values.moneda}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label text='.' htmlFor='button-recalc' md={12} />
                      <Col md={12}>
                        <Button tiny outline block onClick={() => clickRecalcular({ values, setValues })}>
                          Recalcular
                        </Button>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              <FormGroups.FlagCambioMonedaFormGroup iValue={values.flagCambioMoneda} fIsSubmitting={isSubmitting} />
              <Divider />
              <Row>
                <Col md={6}>
                  <FormGroups.FechaPagoFormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.fechaPago}
                    iError={errors.fechaPago}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.MontoRecibidoFormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.montoRecibido}
                    iError={errors.montoRecibido}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <FormGroups.MedioPago1FormGroup iTouched={touched.medioPago1} iError={errors.medioPago1} />
                </Col>
                <Col md={3}>
                  <FormGroups.MontoMedio1FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.montoMedio1}
                    iError={errors.montoMedio1}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.MontoVueltoFormGroup
                    iTouched={touched.montoVuelto}
                    iError={errors.montoVuelto}
                    montoRecibido={values.montoRecibido}
                    montoMedio1={values.montoMedio1}
                    setFieldValue={setFieldValue}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <FormGroups.MedioPago2FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.medioPago2}
                    iError={errors.medioPago2}
                  />
                </Col>
                <Col md={3}>
                  <FormGroups.MontoMedio2FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.montoMedio2}
                    iError={errors.montoMedio2}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.NumeroDocumentoMedio2FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.numeroDocumentoMedio2}
                    iError={errors.numeroDocumentoMedio2}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <FormGroups.MedioPago3FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.medioPago3}
                    iError={errors.medioPago3}
                  />
                </Col>
                <Col md={3}>
                  <FormGroups.MontoMedio3FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.montoMedio3}
                    iError={errors.montoMedio3}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.NumeroDocumentoMedio3FormGroup
                    fIsSubmitting={isSubmitting}
                    iTouched={touched.numeroDocumentoMedio3}
                    iError={errors.numeroDocumentoMedio3}
                  />
                </Col>
              </Row>
              <Divider />
              <FormGroups.MedioDePagoFormGroup
                fIsSubmitting={isSubmitting}
                iTouched={touched.medioDePago}
                iError={errors.medioDePago}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button color='secondary' onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button id='boton-guardar-pago' onClick={handleSubmit} disabled={isSubmitting}>
                Cobrar
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default React.memo(FormularioPagosModal)
